import { useAssetStore } from '@/stores/api/asset';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useAppStore } from '@/stores/appStore';
import { useSelectionStore } from '@/stores/ui/selection';
const TREE_LOADING_CLEAR_DELAY = 1000;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const useNodeManager = () => {
  const selectionStore = useSelectionStore();
  const cruxModuleStore = useCruxModuleStore();
  const appStore = useAppStore();
  const assetStore = useAssetStore();

  /**
   * Deep merge objects (native implementation)
   * @param {Object} target - The target object to merge into
   * @param {Object} source - The source object to merge from
   * @returns {Object} The merged target object
   */
  const deepMerge = (target, source) => {
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          // If the target doesn't have this key or it's not an object, create an empty object
          if (!target[key] || typeof target[key] !== 'object' || Array.isArray(target[key])) {
            target[key] = {};
          }
          deepMerge(target[key], source[key]);
        } else {
          target[key] = source[key];
        }
      }
    }
    return target;
  };

  /**
   * Finds a node by its ID in the design tree
   * @param {string} nodeId - The ID of the node to find
   * @param {Array} nodes - The array of nodes to search in
   * @returns {Object|null} The found node or null if not found
   */
  const findNodeById = (nodeId, nodes) => {
    const allNodes = nodes?.length ? nodes : cruxModuleStore.cruxModules;

    const targetNode = allNodes.find((n) => n.code === nodeId);

    if (!targetNode) {
      return null;
    }

    // Recursively build the children tree
    function buildChildrenTree(parentId) {
      const directChildren = allNodes.filter((n) => n.data && n.data.parentCode === parentId);

      return directChildren.map((child) => ({
        ...child,
        children: buildChildrenTree(child.code),
      }));
    }

    const children = buildChildrenTree(nodeId);

    if (children.length > 0) {
      return {
        ...targetNode,
        children: children,
      };
    }

    return targetNode;
  };

  const findNodesByKey = (key, value, nodes = null, results = null) => {
    if (!results) results = [];

    if (!nodes) {
      nodes = cruxModuleStore.cruxModules;
    }

    for (const node of nodes) {
      if (node[key] === value) {
        results.push(node);
      }
      if (node.children) {
        findNodesByKey(key, value, node.children, results);
      }
    }
    return results;
  };

  /**
   * Finds a node's parent by its ID in the design tree
   * @param {string} nodeId - The ID of the node to find the parent for
   * @param {Array} nodes - The array of nodes to search in
   * @returns {Object|null} The parent node or null if not found
   */
  const findParentNode = (nodeId, nodes) => {
    for (const node of nodes) {
      if (node.children) {
        const childIndex = node.children.findIndex((child) => child.uuid === nodeId);
        if (childIndex !== -1) {
          return node;
        }
        const found = findParentNode(nodeId, node.children);
        if (found) return found;
      }
    }
    return null;
  };

  /**
   * Collects a node and all its descendants (children first, then the node itself).
   */
  const collectNodeCodes = (nodeId) => {
    const codes = [];
    const module = cruxModuleStore.cruxModules.find((m) => m.code === nodeId);
    if (module) {
      const children = cruxModuleStore.cruxModules.filter((m) => m.data && m.data.parentCode === module.code);
      for (const child of children) codes.push(...collectNodeCodes(child.code));
      codes.push(module.code);
    }

    return codes;
  };

  /**
   * Removes a node and its descendants from the design tree
   * @param {string} nodeId - The ID of the node to remove
   */
  const removeNode = async (nodeId) => {
    const codes = collectNodeCodes(nodeId);
    appStore.setTreeObjectLoading(true);
    let response = null;
    try {
      response = await cruxModuleStore.destroy(codes.length > 1 ? codes : nodeId);
      if (codes.includes(selectionStore.selectedObjectId) || codes.includes(selectionStore.selectedMeshId)) {
        selectionStore.clearSelection();
      }
      if (response?.error) throw response.error;

      return true;
    } catch (error) {
      console.warn('Error removing node:', error);
      throw error;
    } finally {
      await sleep(TREE_LOADING_CLEAR_DELAY);
      appStore.setTreeObjectLoading(false);
    }
  };

  /**
   * Check if two objects are deeply equal
   * @param {Object} obj1 - First object to compare
   * @param {Object} obj2 - Second object to compare
   * @returns {boolean} True if objects are deeply equal
   */
  const deepEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true;

    if (obj1 == null || obj2 == null) return obj1 === obj2;

    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

    if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
  };

  /**
   * Updates a crux module's data
   * @param {string} nodeId - The code of the crux module to update
   * @param {Object} updates - The updates to apply to the module's data
   */
  const updateNode = async (nodeId, updates, force = false) => {
    // First try to find the module in the already loaded cruxModules
    let cruxModule = cruxModuleStore.cruxModules.find((module) => module.code === nodeId);
    // If not found in loaded modules, fetch it
    if (!cruxModule) {
      cruxModule = await cruxModuleStore.fetch(nodeId);
    }
    if (cruxModule) {
      // Create a copy of the module to modify
      const updatedModule = { ...cruxModule };

      // Ensure data object exists
      if (!updatedModule.data) {
        updatedModule.data = {};
      }

      // Create a copy of the original module to compare against
      const originalModule = JSON.parse(JSON.stringify(updatedModule));
      // Separate root-level updates from data updates
      const rootUpdates = {};
      const dataUpdates = {};

      // Root-level fields that should not be nested in data
      const rootFields = ['name', 'type', 'version'];

      for (const [key, value] of Object.entries(updates)) {
        if (rootFields.includes(key)) {
          rootUpdates[key] = value;
        } else {
          dataUpdates[key] = value;
        }
      }

      // Apply root-level updates
      Object.assign(updatedModule, rootUpdates);

      // Merge the data updates into the module's data
      if (Object.keys(dataUpdates).length > 0) {
        deepMerge(updatedModule.data, dataUpdates);
      }

      // Only update if the module has actually changed
      if (!deepEqual(originalModule, updatedModule) || force) {
        if (cruxModuleStore.isTracking) {
          await cruxModuleStore.update(nodeId, updatedModule);
        }
      }
    }
  };

  const duplicateNode = async (nodeId) => {
    try {
      await copyAndPasteNode(nodeId, { x: 1, y: 0, z: 0 });
    } catch (error) {
      console.error('Error duplicating node:', error);
    }
  };

  const copyAndPasteNode = async (nodeId, offset = { x: 1, y: 0, z: 0 }) => {
    const node = findNodeById(nodeId);

    async function addNodeAndChildren(node, offset, parentCode = null, isTopLevel = true) {
      const clonedData = JSON.parse(JSON.stringify(node.data || {}));

      // Only apply offset to the top-level node, not to nested children
      if (isTopLevel && clonedData.position) {
        clonedData.position.x += offset.x;
        clonedData.position.y += offset.y;
        clonedData.position.z += offset.z;
      }

      delete clonedData.parentCode;

      const response = await cruxModuleStore.addSceneModule(null, node.type, clonedData, node.name + ' Copy');

      if (parentCode) {
        await moveNode(response.code, parentCode);
      }

      if (node.children && node.children.length > 0) {
        for (const child of node.children) {
          // Pass false for isTopLevel to preserve children's relative positions
          await addNodeAndChildren(child, offset, response.code, false);
        }
      }
    }

    try {
      appStore.setTreeObjectLoading(true);
      await addNodeAndChildren(node, offset, node.data?.parentCode ?? null);
    } finally {
      await sleep(TREE_LOADING_CLEAR_DELAY);
      appStore.setTreeObjectLoading(false);
    }
  };
  /**
   * Moves a node to a new parent in the design tree
   * @param {string} parentCode - The code of the new parent node
   * @param {string} code - The code of the node to move
   */
  const moveNode = async (code, parentCode) => {
    try {
      // Update the crux module's data with the new parentCode
      let cruxModule = cruxModuleStore.cruxModules.find((module) => module.code === code);

      // If not found in loaded modules, fetch it
      if (!cruxModule) {
        cruxModule = await cruxModuleStore.fetch(code, false, {
          asset: assetStore.currentAsset.code,
        });
      }

      if (cruxModule) {
        // Create a copy of the module to modify
        const updatedModule = { ...cruxModule };

        // Ensure data object exists
        if (!updatedModule.data) {
          updatedModule.data = {};
        }

        // Add parentCode to the data
        updatedModule.data.parentCode = parentCode;

        // Update the module using the store's update method
        await cruxModuleStore.update(code, updatedModule);
      }
    } catch (error) {
      console.error('Error moving node:', error);
      throw error;
    }
  };

  return {
    findNodeById,
    findNodesByKey,
    findParentNode,
    removeNode,
    updateNode,
    duplicateNode,
    copyAndPasteNode,
    moveNode,
  };
};
